// Shared base options for every chart component. Keeps the palette, grid
// colours, and font defaults consistent across the six chart types.
//
// Two-tier API:
//   - `baseOption(...)` — full template for axis-cartesian charts
//     (line, bar, area, scatter, etc.). Provides `xAxis` / `yAxis`.
//   - `chartChrome()` / `legendDefaults` / `tooltipTextStyle` /
//     `axisLabelDefaults` / `axisLineDefaults` / `splitLineDefaults` —
//     composable pieces for charts that build their own option from
//     scratch (pie, donut, funnel, gauge, polar, calendar, radar,
//     sankey, sunburst, punchcard). Spreading `...chartChrome()` at
//     the top of any chart option gives it the same colour palette
//     and mono text baseline as the cartesian charts; the remaining
//     pieces are opt-in based on whether the chart has axes / a
//     legend / a tooltip.

import { ref } from "vue";
import { COLORS, FONT_STACK_MONO, PALETTE } from "../theme.ts";
import {
  axisTickFormatter,
  legendLayout,
  plotWidthEstimate,
  tooltipBounds,
} from "./chart-layout";

/**
 * Resolve `--lucent-series-palette` from the host's computed style. The
 * token is a comma-separated colour list (any CSS colour syntax), which
 * we split into an array of trimmed strings. Returns `null` when the
 * variable is absent or empty so callers can fall back to {@link PALETTE}.
 *
 * Same canvas-safety constraint as {@link resolveCssVar}: colours pass
 * straight through to ECharts' canvas backend, so they must already be
 * concrete `#hex` / `rgb()` / `rgba()` strings — not `var(--...)`
 * references. Themes that reference other tokens inside the palette
 * value should make sure those resolve too (browsers do this once;
 * we receive the flattened computed value).
 */
/**
 * The element to read CSS variables from. Themes scope tokens to the
 * `<lucent-insight>` host element (e.g. `lucent-insight { --lucent-chart-text-color: ... }`),
 * NOT to `:root` / `<html>`, so reading from `document.documentElement`
 * silently misses every theme override. CSS custom properties inherit
 * through the shadow DOM boundary, so reading computed style on the
 * host element yields the same values an in-shadow chart element would
 * compute — and it works whether the token is set on the host directly
 * or on `:host` defaults declared by `EmbedHost.vue` inside the shadow.
 *
 * If multiple embeds are mounted on the same page, we pick the first
 * one. Tokens are page-level in practice (the host page declares them
 * once for all embeds it mounts), so a single read is sufficient for
 * the test env and the typical embedder. The fall-through to
 * `document.documentElement` keeps SSR / no-embed contexts safe.
 */
function tokenHost(): Element {
  if (typeof document === "undefined") {
    return null as unknown as Element;
  }
  return (
    document.querySelector("lucent-insight") ?? document.documentElement
  );
}

/**
 * Bumped whenever the page theme changes, so every chart's `option`
 * computed re-runs and re-reads the colour tokens. Chart options are
 * Vue `computed`s whose only reactive inputs are props — the token
 * reads below are `getComputedStyle` calls Vue can't track — so
 * without this a light/dark toggle left charts painted in the old
 * theme's colours until the next data refresh.
 *
 * Watches `data-theme` / `class` / `style` on `<html>` (the dashboard
 * shells flip `data-theme`; embedders commonly flip a class) plus the
 * OS `prefers-color-scheme`. Installed lazily on first token read.
 */
const themeEpoch = ref(0);
let themeWatchInstalled = false;
function trackTheme(): void {
  void themeEpoch.value;
  if (themeWatchInstalled || typeof window === "undefined") return;
  themeWatchInstalled = true;
  const bump = () => {
    themeEpoch.value++;
  };
  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(bump).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "class", "style"],
    });
  }
  window
    .matchMedia?.("(prefers-color-scheme: dark)")
    ?.addEventListener?.("change", bump);
}

function resolveSeriesPalette(): string[] | null {
  if (typeof window === "undefined") return null;
  trackTheme();
  const raw = getComputedStyle(tokenHost())
    .getPropertyValue("--lucent-series-palette")
    .trim();
  if (!raw) return null;
  const colours = raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return colours.length > 0 ? colours : null;
}

/**
 * Resolve a single colour token from the embed host's computed style.
 * Returns `undefined` when the token is absent so the ECharts defaults
 * can take over.
 */
function resolveColour(name: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  trackTheme();
  const v = getComputedStyle(tokenHost()).getPropertyValue(name).trim();
  return v || undefined;
}

/**
 * Resolve a CSS-variable reference to a concrete value. Canvas can't
 * read `var(--…)` directly — ECharts paints to canvas (renderer pinned
 * for round-4 security M-5), so any token-style colour fed into series
 * `itemStyle`, `lineStyle`, label `color`, etc. has to be flattened to
 * hex / rgb / rgba before the paint or canvas silently treats it as
 * invalid (typically paints nothing or transparent).
 *
 * Accepts:
 *   - `var(--token-name)` — looks up `--token-name` on the
 *     `<lucent-insight>` host element (themes scope tokens to the host,
 *     not `:root`; see `tokenHost()` above).
 *   - `var(--token-name, fallback)` — strips the fallback before
 *     lookup; if the variable isn't declared, the caller's own
 *     `?? defaultColor` provides the canvas-safe value.
 *   - any other string — passes through unchanged, so callers can hand
 *     a raw hex or token reference interchangeably.
 *
 * Returns `undefined` when no value is recoverable, so callers can
 * `?? CHART_COLORS.foo` and stay canvas-safe.
 */
export function resolveCssVar(input: string | undefined): string | undefined {
  if (!input) return undefined;
  // Match `var(--name)` or `var(--name, fallback)` — keep only the
  // token name, drop the in-line fallback (the caller handles that).
  const m = /^var\(\s*(--[\w-]+)(?:\s*,[^)]*)?\)$/.exec(input.trim());
  if (!m) return input;
  if (typeof window === "undefined") return undefined;
  trackTheme();
  // Same host-vs-:root issue as `resolveColour` — tokens live on the
  // `<lucent-insight>` element, not `<html>`.
  const v = getComputedStyle(tokenHost()).getPropertyValue(m[1]!).trim();
  return v || undefined;
}

export interface BaseOptionInput {
  title?: string;
  yFormat?: string;
  xFormat?: string;
  /**
   * Series names the legend will show, when it shows. Lets the grid
   * reserve the rows a wrapped legend actually takes (print, narrow).
   */
  legendNames?: ReadonlyArray<string>;
}

/** Optional chrome opts. */
export interface ChartChromeOpts {
  /**
   * Print-mode flag. When true, `animation: false` is included at the
   * top level so ECharts paints the chart at its final values
   * immediately — no bar grow, no line draw-in, no easing. The PDF
   * renderer captures right after the first paint, so any in-flight
   * animation would land in the PDF as half-drawn geometry with
   * final-value axis labels. Off by default — interactive embeds
   * keep their original feel.
   */
  print?: boolean;
  /**
   * Measured chart width, px (0 / absent = unmeasured → legacy layout).
   * See `chart-layout.ts` and `useChartLayout`.
   */
  width?: number;
  /** Coarse pointer (touch). */
  coarse?: boolean;
}

/**
 * Theme-aware text colours for per-chart labels that sit outside the
 * shared legend / axis blocks (sankey node labels, gauge detail,
 * heatmap visual-map). Reading the token rather than `COLORS.*`
 * keeps them legible when the host flips to a dark theme.
 */
export const chartTextColor = () =>
  resolveColour("--lucent-chart-text-color") ?? COLORS.text;
export const chartLabelColor = () =>
  resolveColour("--lucent-chart-label-color") ?? COLORS.chartLabelMuted;
export const chartLegendColor = () =>
  resolveColour("--lucent-chart-legend-color") ?? COLORS.chartLabelStrong;

/**
 * Top-level option fields that should appear on every chart
 * regardless of coordinate system. Spread this at the top of any
 * chart's option object — `color` / `backgroundColor` / `textStyle`
 * are all top-level ECharts fields and don't conflict with
 * `tooltip` / `legend` / `series` / axis blocks the caller adds.
 *
 * `textStyle` is the inherited baseline for any text the chart paints
 * via canvas (slice labels, axis names, etc.) that doesn't have a
 * more specific style attached.
 */
export const chartChrome = (opts: ChartChromeOpts = {}) => ({
  // `--lucent-series-palette` overrides the built-in `PALETTE` when
  // the theme supplies one. Falls back to `PALETTE` when the token
  // isn't declared or parses to an empty list.
  color: resolveSeriesPalette() ?? [...PALETTE],
  backgroundColor: "transparent",
  textStyle: {
    // `--lucent-chart-text-color` / `--lucent-chart-font-family`
    // override the editorial defaults so dark themes (Linear /
    // Vercel) and webfont-driven themes (Notion / Slack / Stripe)
    // can pick up the host page's stack instead of the locked-in
    // mono baseline.
    color: chartTextColor(),
    fontFamily:
      resolveColour("--lucent-chart-font-family") ?? FONT_STACK_MONO,
    fontSize: 11,
  },
  ...(opts.print ? { animation: false as const } : {}),
});

/**
 * Mono font baseline for ECharts' HTML tooltip overlay. Tooltips
 * aren't painted to canvas — they're real DOM nodes whose font
 * picks up the host page's stack by default. Spread this into any
 * per-chart `tooltip.textStyle` to keep the hover readout in the
 * same editorial register as the rest of the chart.
 *
 * Function rather than const so themes that override
 * `--lucent-chart-font-family` take effect on every chart build.
 */
export const tooltipTextStyle = () => ({
  fontFamily:
    resolveColour("--lucent-chart-font-family") ?? FONT_STACK_MONO,
  fontSize: 11,
});

/**
 * Tooltip defaults every chart shares: the mono text style plus
 * `confine` and a width cap, so a long tooltip (pie "Other" members,
 * heatmap cells, tree paths) stays inside the chart instead of running
 * off a phone screen. Spread it, then add a per-chart `formatter`.
 */
export const tooltipDefaults = (
  trigger: "item" | "axis",
  opts: ChartChromeOpts = {},
) => ({
  trigger,
  ...(trigger === "axis" ? { axisPointer: { type: "shadow" as const } } : {}),
  textStyle: tooltipTextStyle(),
  ...tooltipBounds(opts.width ?? 0),
});

/**
 * Editorial legend treatment — uppercase + tracked + warm charcoal,
 * with circle markers and the strip pushed 10px down from the chart
 * top so it doesn't sit flush against the eyebrow header in
 * `InsightChart.vue`.
 *
 * Charts that want the legend at the bottom (pie / donut / radar)
 * spread this then override `top: undefined` and `bottom: 0`.
 *
 * Function rather than const so themes that override
 * `--lucent-chart-legend-color` / `--lucent-chart-font-family` /
 * `--lucent-chart-legend-case` take effect on every chart build.
 * `--lucent-chart-legend-case: none` switches the formatter from
 * `UPPERCASE` to as-typed.
 *
 * The legend is `type: "scroll"`: it stays one row (or column) and
 * pages with arrows instead of wrapping into rows that run over the
 * plot — grids and pie centres only reserve room for one row. Print
 * can't page, so `print: true` keeps the plain wrapping legend and the
 * PDF shows every item.
 */
export const legendDefaults = (opts: ChartChromeOpts = {}) => {
  const uppercase =
    (resolveColour("--lucent-chart-legend-case") ?? "uppercase") ===
    "uppercase";
  return {
    type: (opts.print ? "plain" : "scroll") as "plain" | "scroll",
    pageIconColor: chartLegendColor(),
    pageIconInactiveColor: chartLabelColor(),
    pageIconSize: 9,
    pageTextStyle: {
      color: chartLegendColor(),
      fontFamily:
        resolveColour("--lucent-chart-font-family") ?? FONT_STACK_MONO,
      fontSize: 10,
    },
    top: 10 as number | undefined,
    bottom: undefined as number | undefined,
    textStyle: {
      color: chartLegendColor(),
      fontFamily:
        resolveColour("--lucent-chart-font-family") ?? FONT_STACK_MONO,
      fontSize: 10,
      fontWeight: 600,
    },
    itemGap: 18,
    itemWidth: 10,
    itemHeight: 10,
    icon: "circle" as const,
    formatter: uppercase
      ? (name: string) => name.toUpperCase()
      : (name: string) => name,
  };
};

/**
 * Default `axisLabel` style — warm mid-grey mono, sized to match
 * the rest of the chart text. Spread into any axis (xAxis / yAxis /
 * angleAxis / radiusAxis) that wants the standard tick treatment.
 *
 * Function rather than const so themes that override
 * `--lucent-chart-label-color` / `--lucent-chart-font-family` take
 * effect on every chart build.
 */
export const axisLabelDefaults = () => ({
  color: chartLabelColor(),
  fontFamily:
    resolveColour("--lucent-chart-font-family") ?? FONT_STACK_MONO,
  fontSize: 11,
});

/**
 * Warm baseline rule for the "floor" axis (X axis on cartesian
 * charts, angle axis on polar). Slightly stronger than the dashed
 * Y-guides so the data anchors visually.
 *
 * Now a getter so themes that override `--lucent-chart-axis-color`
 * (including `transparent` to hide the axis entirely) take effect
 * on every chart-options build, not just the first.
 */
export const axisLineDefaults = () => ({
  lineStyle: {
    color: resolveColour("--lucent-chart-axis-color") ?? COLORS.chartAxisLine,
    width: 1,
  },
});

/**
 * Warm dashed gridlines for the value axis. Reads as "guide"
 * rather than "rule" — lets the data series dominate.
 *
 * Now a getter; same rationale as `axisLineDefaults`.
 */
export const splitLineDefaults = () => {
  const styleRaw = resolveColour("--lucent-chart-grid-style") ?? "dashed";
  const type =
    styleRaw === "solid" || styleRaw === "dotted" || styleRaw === "dashed"
      ? styleRaw
      : ("dashed" as const);
  return {
    lineStyle: {
      color: resolveColour("--lucent-chart-grid-color") ?? COLORS.chartGridLine,
      type,
      width: 1,
    },
  };
};

/**
 * Axis label styling tightened for print: darker text, slightly
 * bolder, marginally larger. ECharts paints axis labels to canvas
 * (since the renderer is pinned to canvas), so CSS in
 * `print-styles.css` can't reach them — the only way to make them
 * legible on paper is to pass darker tones through the option here.
 *
 * Spreads on top of `axisLabelDefaults` so non-print charts are
 * unaffected; print-mode charts get the override fields.
 */
const axisLabelPrintOverrides = {
  color: COLORS.text,
  fontWeight: 600 as const,
  fontSize: 11,
};

/**
 * Split-line ramp slightly stronger in print so the dashed Y guides
 * survive a paper render. Same reasoning as axis labels — these
 * paint to canvas, so CSS doesn't reach.
 */
const splitLinePrintOverrides = {
  lineStyle: {
    color: COLORS.chartAxisLine,
    type: "dashed" as const,
    width: 1,
  },
};

export function baseOption(input: BaseOptionInput, opts: ChartChromeOpts = {}) {
  // Title is rendered as an HTML eyebrow header in `InsightChart.vue`,
  // not inside the canvas. ECharts' built-in title gives us no real
  // typographic control (font, casing, letter-spacing all live behind
  // a small option surface), so hoisting it out lets the title use
  // the same mono / uppercase treatment as the table headers and KPI
  // labels. `input.title` is still part of the API for callsite
  // stability — we just drop it on the floor here.
  //
  // `opts.print` propagates into `chartChrome()` to disable animation
  // — every cartesian chart spreads `baseOption(...)` at the top of
  // its option, so passing the flag here inherits it for free.
  const labelStyle = opts.print
    ? { ...axisLabelDefaults(), ...axisLabelPrintOverrides }
    : axisLabelDefaults();
  const splitLineStyle = opts.print
    ? splitLinePrintOverrides
    : splitLineDefaults();
  const width = opts.width ?? 0;
  const plotWidth = plotWidthEstimate(width);
  const legend = legendLayout(input.legendNames ?? [], { width, print: !!opts.print });
  return {
    ...chartChrome(opts),
    // `containLabel` sizes the margins to the actual tick labels, so a
    // long y tick ("$1,200,000") no longer clips at a fixed 56px gutter
    // and short ones don't waste it. It ignores axis *names* — charts
    // with named axes add room for them. Top reserves the legend: one
    // row is the historical 38px; a wrapped legend (print, narrow
    // screens) reserves every row it takes.
    grid: {
      containLabel: true,
      left: 8,
      right: 16,
      top: legend.reserveTop,
      bottom: 8,
    },
    tooltip: tooltipDefaults("axis", opts),
    legend: { ...legendDefaults(opts), type: legend.type, itemGap: legend.itemGap },
    xAxis: {
      ...axisLineDefaults(),
      axisTick: { show: false },
      axisLabel: {
        ...labelStyle,
        formatter: axisTickFormatter(input.xFormat, plotWidth),
      },
    },
    yAxis: {
      axisLine: { show: false },
      splitLine: { ...splitLineStyle },
      axisLabel: {
        ...labelStyle,
        // Narrow plots compact large ticks ("1.2M") — see
        // `axisTickFormatter`.
        formatter: axisTickFormatter(input.yFormat, plotWidth),
      },
    },
  };
}
